/**
 * Builds the `Services Section Clicked` payload for a pro-profile prices interaction (#5617).
 * Pure and framework-free, so the whole null/price matrix is unit-tested in isolation.
 *
 * Every property the spec calls optional is sent as an explicit `null` rather than omitted:
 * PostHog filters "not set" separately from "null", so a dropped property is a different event.
 */

import { ServicesSectionClickedProperties, ServicesSectionItem } from 'Douleutaras/app/lib/posthog/posthog.types';
import { isDisplayablePrice } from 'Douleutaras/functional-components/ServicePrices/priceFormatter';
import { PriceType, ServicePrice } from 'Douleutaras/functional-components/ServicePrices/types';

import { CatalogueNodeTracking } from './types';

// The one null literal in this module; everything else routes through it
// tslint:disable-next-line:no-null-keyword
const NULL_VALUE: null = null;

const orNull = <T>(value: T | undefined): T | null => value ?? NULL_VALUE;

// Interactions on the category header itself, which name no service or detail node
const CATEGORY_LEVEL_ITEMS: ReadonlyArray<ServicesSectionItem> = [
  ServicesSectionItem.CATEGORY_EXPAND,
  ServicesSectionItem.CATEGORY_INFO
];

type PriceProperties = Pick<
  ServicesSectionClickedProperties,
  'price_min' | 'price_max' | 'price_type' | 'price_unit'
>;

interface ServicesSectionClickArgs {
  item: ServicesSectionItem;
  tracking: CatalogueNodeTracking;
  /** The clicked row's price. Category-level interactions pass none. */
  price?: ServicePrice;
}

/**
 * The price group as displayed on the clicked row, or all-null when the row shows no amount.
 * Amounts are the ex-VAT figures the row renders — the `+ VAT` subline is static copy, so
 * nothing is derived here.
 */
const buildPriceProperties = (price?: ServicePrice): PriceProperties => {
  if (!isDisplayablePrice(price)) {
    return {
      price_min: NULL_VALUE,
      price_max: NULL_VALUE,
      price_type: NULL_VALUE,
      price_unit: NULL_VALUE
    };
  }

  return {
    price_min: price.amount,
    // Only a range renders an upper bound
    price_max: price.priceType === PriceType.RANGE ? orNull(price.amountMax) : NULL_VALUE,
    price_type: price.priceType,
    price_unit: price.priceUnit
  };
};

export const buildServicesSectionClickedProperties = ({
  item,
  tracking,
  price
}: ServicesSectionClickArgs): ServicesSectionClickedProperties => ({
  item_clicked: item,
  // A category-level interaction names no single node, so there is nothing for the form to have mapped
  is_form_mapped: CATEGORY_LEVEL_ITEMS.includes(item)
    ? NULL_VALUE
    : tracking.isFormMapped ?? false,
  category_id: tracking.categoryId,
  category_title: tracking.categoryTitle,
  service_id: orNull(tracking.serviceId),
  service_title: orNull(tracking.serviceTitle),
  detail_id: orNull(tracking.detailId),
  detail_title: orNull(tracking.detailTitle),
  ...buildPriceProperties(price)
});
